import { BreadCrumbJsonLdProps } from 'next-seo'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { CountryAndLocale } from '../../external'
import { Catalog, Product } from '../../utils/types/Product'
import { getBasePathWithLocale } from '../../utils/urls'
import useUrls from '../../services/useUrls'
import { ParentPointer } from '../../utils/types/misc'
import { getPathName } from '../../lib/product'

interface UseBreadcrumbSchemaPropsParams {
  categories?: Catalog[] | ParentPointer[]
  product?: Product
  productName?: string
}

const useBreadcrumbSchemaProps = (
  params: UseBreadcrumbSchemaPropsParams,
): BreadCrumbJsonLdProps => {
  const { categories, product, productName } = params

  const { locale: countryAndLocale } = useRouter()
  const { urlT } = useUrls()
  const { t } = useTranslation('products')

  const basePath = getBasePathWithLocale(countryAndLocale as CountryAndLocale)
  const productPath = product ? getPathName(product, t) : ''
  const productUrl = `${basePath}${productPath}`

  if (!categories || categories?.length === 0) return {} as BreadCrumbJsonLdProps

  // Typeguard to check wether catagories is Catalog[] or ParentPointer[] type
  const isParentPointer = Object.keys(categories?.[0] || {}).includes('code')

  // Assumes categories are in correct order and "Home" is removed
  const topLevelCategoryCode = isParentPointer
    ? (categories as ParentPointer[])?.[0].code
    : (categories as Catalog[])?.[0]['catalog.code']

  const itemListElements = categories?.map((category, index) => {
    const categoryCode = isParentPointer
      ? (category as ParentPointer).code
      : (category as Catalog)['catalog.code']
    const categoryLabel = isParentPointer
      ? (category as ParentPointer).label
      : (category as Catalog)['catalog.label']
    const isTopLevelCategory = categoryCode === topLevelCategoryCode

    return {
      position: index + 1,
      name: categoryLabel,
      item: isTopLevelCategory
        ? urlT(`${basePath}/products/category/${topLevelCategoryCode}`)
        : urlT(`${basePath}/products/category/${topLevelCategoryCode}/${categoryCode}`),
    }
  }) || []

  // Only runs in product pages
  if (product) {
    itemListElements?.push({
      position: itemListElements.length + 1,
      name: productName || '',
      item: productUrl,
    })
  }

  return {
    itemListElements,
  }
}

export default useBreadcrumbSchemaProps
